import { SearchOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear'; // Import du logo pour effacer
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, CircularProgress, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import ButtonIcon from 'components/ButtonIcon';
import ButtonIconText from 'components/ButtonIconText';
import LanguageContext from 'context/LanguageContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface OrganizationFilterProps {
  handleSearch: (value: string, searchType: string) => void;
  handleDownloadBusinessData: () => void;
  isLoading: boolean;
  loadingDownloadCSV: boolean;
  isCreateBusiness: boolean;
  title: string;
  period?: 'month' | 'semester' | 'year';
  onPeriodChange?: (period: 'month' | 'semester' | 'year') => void;
}

const OrganizationFilter = ({ handleSearch, handleDownloadBusinessData, isLoading, loadingDownloadCSV, isCreateBusiness, title, period = 'month', onPeriodChange }: OrganizationFilterProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // language direction 
  const { language } = useContext(LanguageContext);
  const [value, setValue] = useState("");
  const [searchType, setSearchType] = useState("business");

  const handleChangeSearchText = (e: any) => {
    setValue(e.target.value);
    handleSearch(e.target.value, searchType);
  };

  const handleSearchTypeChange = (e: any) => {
    setSearchType(e.target.value);
    //handleSearch(value, e.target.value);
  };

  const handleClear = () => {
    setValue("");
    handleSearch("", searchType);
  };

  const handlePeriodChange = (e: any) => {
    if (onPeriodChange) {
      onPeriodChange(e.target.value);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: "144px",
        mb: "22px",
        flexWrap: 'wrap',

      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box display={'flex'} alignItems={"center"} gap={"2rem"} flexWrap={"nowrap"} width={{ xs: "100%", md: "80%" }} >
          <Box>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '24px',
                lineHeight: '31px',
                color: '#2F3B49',
              }}
            >
              {title}
            </Typography>
          </Box>
          {isCreateBusiness && <Box  >
            <ButtonIconText
              color='primary'
              disableElevation={true}
              sx={{
                width: '190px',
              }}
              type='submit'
              variant='contained'
              size='large'
              onClick={(e: any) => {
                e.preventDefault();
                navigate('/admin/add-business');
              }}
            >
              {t('ADMIN_DASHBOARD_ADD_ORGANISATION')}
            </ButtonIconText>
          </Box>}
          <Box width={"100%"}>
            <TextField
              autoComplete='off'
              id='standard-bare'
              size='small'
              fullWidth={true}
              variant='outlined'
              placeholder={t('ADMIN_STATS_SEARCH_PLACEHOLDER')}
              sx={{
                width: { md: "100%", lg: "590px" },
                '& .MuiInputBase-root': {
                  WebkitFontSmoothing: 'auto',
                  padding: "0px",

                },
                '& .MuiInputBase-inputAdornedStart': {
                  padding: 0, // Enlever le padding
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FormControl sx={{ mr: 1, }}>
                      <Select
                        value={searchType}
                        onChange={handleSearchTypeChange}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root': {
                                fontSize: '16px',
                              },
                            },
                          },
                        }}
                        sx={{
                          height: '50px',
                          width: '109px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '4px 0px 0px 4px',
                          borderRight: '1px solid #C4C4C4',

                          '& .MuiSelect-select': {
                            padding: '8px 10px 8px 8px',
                            fontSize: '16px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        <MenuItem value="business">{t('ADMIN_STATS_SEARCH_BY_ORGANIZATION')}</MenuItem>
                        <MenuItem value="organizationId">{t('ADMIN_STATS_SEARCH_BY_ORGANIZATION_ID')}</MenuItem>
                      </Select>
                    </FormControl>
                    {value ?
                      (
                        <IconButton onClick={handleClear}>
                          <ClearIcon />
                        </IconButton>
                      ) : (
                        <IconButton disabled>
                          <SearchOutlined />
                        </IconButton>
                      )}
                  </InputAdornment>
                ),
                endAdornment: isLoading ? (
                  <Box display="flex" mx={1} >
                    <CircularProgress size={24} />
                  </Box>
                ) : null,
                sx: { height: '50px' } // Augmenter la hauteur de l'input
              }}
              value={value}
              onChange={handleChangeSearchText}
            />
          </Box>
        </Box>

        <Box display={'flex'} alignItems={"center"} gap={2}>
          {/* Period Selector */}
          {onPeriodChange && (
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={period}
                onChange={handlePeriodChange}
                size="small"
                sx={{
                  height: '40px',
                  backgroundColor: '#FFFFFF',
                  '& .MuiSelect-select': {
                    fontSize: '14px',
                  },
                }}
              >
                <MenuItem value="month">{t('PERIOD_MONTH')}</MenuItem>
                <MenuItem value="semester">{t('PERIOD_SEMESTER')}</MenuItem>
                <MenuItem value="year">{t('PERIOD_YEAR')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <ButtonIcon
            onClick={handleDownloadBusinessData}
            disabled={loadingDownloadCSV}
          >
            {loadingDownloadCSV ? (
              <CircularProgress size={24} />
            ) : (
              <FileDownloadOutlinedIcon fontSize='large' />
            )}
          </ButtonIcon>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationFilter;